import React, { useState, useRef, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Search, Camera, X, CreditCard, MessageSquare,
  Phone, MapPin, Tv, Wifi, User, Hash, CheckCircle,
  XCircle, AlertCircle, Banknote
} from 'lucide-react';
import { customerAPI, paymentAPI, complaintAPI } from '../services/api';
import useDarkReady from '../theme/useDarkReady';

const SCANNER_ID = 'search-scanner';

function PaymentModal({ customer, onClose }) {
  const planPrice = customer.catv?.package?.price || customer.broadband?.plan?.price || '';
  const [form, setForm] = useState({
    paymentFor: (customer.customerType === 'catv') ? 'catv_renewal' : 'broadband_renewal',
    amount: String(planPrice),
    paymentMethod: 'cash',
    transactionId: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      const { data } = await paymentAPI.create({ customer: customer._id, ...form, amount: parseFloat(form.amount) });
      toast.success(`Payment recorded! Receipt: ${data.data.receiptNumber}`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '.88rem', boxSizing: 'border-box' };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Record Payment — {customer.name}</h3>
          <button className="modal-close" onClick={() => onClose(false)}>✕</button>
        </div>
        <form onSubmit={handlePay}>
          <div className="modal-body">
            <div style={{ background: 'var(--cb-f0f9ff)', border: '1px solid var(--cb-bae6fd)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: '.85rem', color: 'var(--c-0369a1)' }}>
              Plan rate: <strong>
                ₹{customer.catv?.package?.price || customer.broadband?.plan?.price || '—'}/mo
              </strong> — edit amount below if customer pays different
            </div>
            <div className="form-group">
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Payment For</label>
              <select style={inputStyle} value={form.paymentFor} onChange={e => setForm(f => ({ ...f, paymentFor: e.target.value }))}>
                {(customer.customerType === 'catv') && <option value="catv_renewal">CATV Renewal</option>}
                {(customer.customerType === 'broadband') && <option value="broadband_renewal">Broadband Renewal</option>}
                <option value="installation">Installation</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Amount (₹) *</label>
                <input style={{ ...inputStyle, fontSize: '1.05rem', fontWeight: 700 }} type="number" value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required placeholder="0" />
              </div>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Method</label>
                <select style={inputStyle} value={form.paymentMethod} onChange={e => setForm(f => ({ ...f, paymentMethod: e.target.value }))}>
                  <option value="cash">Cash</option>
                  <option value="upi">UPI</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="staff_collection">Staff Collection</option>
                  <option value="payment_gateway">Payment Gateway</option>
                </select>
              </div>
            </div>
            {(form.paymentMethod === 'upi' || form.paymentMethod === 'bank_transfer' || form.paymentMethod === 'payment_gateway') && (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Transaction ID <span style={{ color: 'var(--c-9ca3af)', fontWeight: 400 }}>(optional)</span></label>
                <input style={inputStyle} className="font-mono" value={form.transactionId} onChange={e => setForm(f => ({ ...f, transactionId: e.target.value }))} placeholder="UTR / reference number — leave blank if unknown" />
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes (optional)</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. partial payment, advance..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Cancel</button>
            <button type="submit" className="btn btn-success" disabled={loading}>
              <CreditCard size={14} /> {loading ? 'Processing...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ComplaintModal({ customer, onClose }) {
  const [form, setForm] = useState({ category: 'technical', description: '', priority: 'medium' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await complaintAPI.create({ customer: customer._id, ...form });
      toast.success('Complaint raised successfully');
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to raise complaint');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '.88rem', boxSizing: 'border-box' };

  return (
    <div className="modal-overlay" onClick={() => onClose(false)}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Raise Complaint — {customer.name}</h3>
          <button className="modal-close" onClick={() => onClose(false)}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Category</label>
                <select style={inputStyle} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="technical">Technical</option>
                  <option value="billing">Billing</option>
                  <option value="signal">Signal Issue</option>
                  <option value="no_signal">No Signal</option>
                  <option value="stb_issue">STB Issue</option>
                  <option value="plan_change">Plan Change</option>
                  <option value="disconnection">Disconnection</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
                <select style={inputStyle} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: '.8rem', fontWeight: 600, display: 'block', marginBottom: 4 }}>Description *</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} rows={4} required value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => onClose(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <MessageSquare size={14} /> {loading ? 'Submitting...' : 'Raise Complaint'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SearchPage() {
  useDarkReady();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('mobile');
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const scannerRef = useRef(null);
  const didAutoSearch = useRef(false);

  const stopScan = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current = null; } } catch {}
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q && !didAutoSearch.current) {
      didAutoSearch.current = true;
      setQuery(q);
      doSearch(q, 'mobile');
    }
  }, [location.search]); // doSearch is stable within the component lifecycle

  const startScan = async () => {
    setScanning(true);
    setCustomer(null);
    await new Promise(r => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 120 } },
        (decoded) => {
          stopScan();
          setQuery(decoded.trim());
          setSearchType('stb');
          doSearch(decoded.trim(), 'stb');
        },
        () => {}
      );
    } catch {
      toast.error('Camera unavailable — enter STB serial manually');
      stopScan();
    }
  };

  const doSearch = async (q, type) => {
    const term = (q || query).trim();
    const stype = type || searchType;
    if (!term) { toast.error('Enter a search term'); return; }
    setLoading(true);
    setCustomer(null);
    try {
      let c;
      if (stype === 'stb') {
        const res = await customerAPI.getByStb(term);
        c = res.data.data;
      } else {
        const res = await customerAPI.getAll({ search: term, limit: 1 });
        if (!res.data.data?.length) throw Object.assign(new Error('Not found'), { response: { status: 404 } });
        c = res.data.data[0];
      }
      setCustomer(c);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('No customer found');
      } else {
        toast.error(err.response?.data?.message || 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') doSearch(); };

  const reset = () => { stopScan(); setCustomer(null); setQuery(''); };

  const catvStatus = customer?.catv?.isActive;
  const bbStatus = customer?.broadband?.isActive;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const expiryInfo = (d) => {
    if (!d) return null;
    const days = Math.ceil((new Date(d) - new Date()) / 86400000);
    if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, color: 'var(--c-ef4444)', bg: 'var(--cb-fee2e2)' };
    if (days <= 7) return { label: `Expires in ${days}d`, color: 'var(--c-f97316)', bg: 'var(--cb-fff7ed)' };
    return { label: `${days}d left`, color: 'var(--c-16a34a)', bg: 'var(--cb-f0fdf4)' };
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Search Customer</h2>
          <p className="text-muted text-small">Search by mobile, STB serial, or account number — or scan barcode</p>
        </div>
      </div>

      {/* Search bar */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <select
            style={{ padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.88rem', background: 'var(--cb-ffffff)', minWidth: 150 }}
            value={searchType} onChange={e => setSearchType(e.target.value)}>
            <option value="mobile">Mobile Number</option>
            <option value="stb">STB Serial</option>
            <option value="account">Account Number</option>
          </select>
          <div style={{ flex: 1, position: 'relative', minWidth: 200 }}>
            <input
              style={{ width: '100%', padding: '9px 12px 9px 36px', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '.9rem', boxSizing: 'border-box' }}
              placeholder={searchType === 'mobile' ? 'Enter mobile number...' : searchType === 'stb' ? 'Enter STB serial...' : 'Enter account no (ISP00001)...'}
              value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown}
              autoFocus
            />
            <Search size={15} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          </div>
          <button className="btn btn-primary" onClick={() => doSearch()} disabled={loading} style={{ minWidth: 100 }}>
            {loading ? 'Searching...' : <><Search size={14} /> Search</>}
          </button>
          <button className="btn btn-secondary" onClick={startScan} disabled={scanning} title="Scan STB barcode">
            <Camera size={16} /> {scanning ? 'Scanning...' : 'Scan'}
          </button>
          {customer && (
            <button className="btn btn-secondary" onClick={reset} title="Clear">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Camera preview */}
        {scanning && (
          <div style={{ marginTop: 14 }}>
            <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: 8 }}>Point camera at STB barcode</p>
            <div id={SCANNER_ID} style={{ width: '100%', maxWidth: 400, borderRadius: 10, overflow: 'hidden' }} />
            <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={stopScan}>Cancel Scan</button>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>}

      {/* 3-column customer result */}
      {customer && !loading && (
        <div className="search-result-grid">

          {/* Column 1 — Customer Identity */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--c-ffffff)', fontWeight: 700, fontSize: '1rem' }}>
                {customer.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>{customer.name}</div>
                <span className={`badge badge-${customer.status === 'active' ? 'success' : customer.status === 'suspended' ? 'warning' : 'danger'}`} style={{ textTransform: 'capitalize', fontSize: '.7rem' }}>
                  {customer.status}
                </span>
              </div>
            </div>
            <InfoRow icon={<Phone size={13} />} label="Mobile" value={customer.mobile || '—'} />
            {customer.alternateMobile && <InfoRow icon={<Phone size={13} />} label="Alt Mobile" value={customer.alternateMobile} />}
            <InfoRow icon={<MapPin size={13} />} label="Address"
              value={[customer.address?.line1, customer.address?.city].filter(Boolean).join(', ') || '—'}
            />
            <InfoRow icon={<MapPin size={13} />} label="Society"
              value={customer.address?.area || '—'}
            />
            <InfoRow icon={<Hash size={13} />} label="House No"
              value={customer.broadband?.houseNumber || customer.catv?.houseNumber || '—'}
            />
            <InfoRow icon={<Hash size={13} />} label="Account No" value={customer.customerId} mono />
            <InfoRow icon={<User size={13} />} label="Type"
              value={<span style={{ textTransform: 'capitalize' }}>{customer.customerType}</span>}
            />
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/customers/${customer._id}`)} style={{ width: '100%', justifyContent: 'center' }}>
                View Full Profile →
              </button>
            </div>
          </div>

          {/* Column 2 — Subscription Info */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: '.85rem', marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Subscription
            </div>

            {/* CableTV */}
            {(customer.customerType === 'catv') && (
              <div style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <Tv size={14} color="var(--primary)" />
                  <span style={{ fontWeight: 600, fontSize: '.9rem' }}>CableTV</span>
                  {catvStatus
                    ? <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-16a34a)', fontSize: '.8rem', fontWeight: 600 }}><CheckCircle size={13} /> Active</span>
                    : <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-ef4444)', fontSize: '.8rem', fontWeight: 600 }}><XCircle size={13} /> Inactive</span>
                  }
                </div>
                <InfoRow label="STB Serial" value={customer.catv?.setTopBoxSerial || '—'} mono />
                <InfoRow label="Package" value={customer.catv?.package?.displayName || customer.catv?.package?.name || '—'} />
                <InfoRow label="Rate" value={customer.catv?.package?.price ? `₹${customer.catv.package.price}/mo` : '—'} bold />
                {(() => {
                  const info = expiryInfo(customer.catv?.expiryDate);
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Expiry</span>
                      {info
                        ? <span style={{ fontSize: '.82rem', fontWeight: 600, color: info.color, background: info.bg, padding: '1px 8px', borderRadius: 12 }}>{info.label}</span>
                        : <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>{fmtDate(customer.catv?.expiryDate)}</span>
                      }
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Broadband */}
            {(customer.customerType === 'broadband') && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <Wifi size={14} color="var(--primary)" />
                  <span style={{ fontWeight: 600, fontSize: '.9rem' }}>Broadband</span>
                  {bbStatus
                    ? <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-16a34a)', fontSize: '.8rem', fontWeight: 600 }}><CheckCircle size={13} /> Active</span>
                    : <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-ef4444)', fontSize: '.8rem', fontWeight: 600 }}><XCircle size={13} /> Inactive</span>
                  }
                </div>
                <InfoRow label="Plan" value={customer.broadband?.plan?.displayName || customer.broadband?.plan?.name || '—'} />
                <InfoRow label="Rate" value={customer.broadband?.plan?.price ? `₹${customer.broadband.plan.price}/mo` : '—'} bold />
                {(() => {
                  const info = expiryInfo(customer.broadband?.expiryDate);
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Expiry</span>
                      {info
                        ? <span style={{ fontSize: '.82rem', fontWeight: 600, color: info.color, background: info.bg, padding: '1px 8px', borderRadius: 12 }}>{info.label}</span>
                        : <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>{fmtDate(customer.broadband?.expiryDate)}</span>
                      }
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Outstanding balance */}
            {customer.outstandingBalance > 0 && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--cb-fee2e2)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={16} color="var(--c-ef4444)" />
                <span style={{ color: 'var(--c-dc2626)', fontWeight: 700, fontSize: '.9rem' }}>
                  Outstanding: ₹{customer.outstandingBalance.toLocaleString('en-IN')}
                </span>
              </div>
            )}
          </div>

          {/* Column 3 — Actions */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <div style={{ fontWeight: 700, fontSize: '.85rem', marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
              Actions
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Payment */}
              <div style={{ padding: '14px', background: 'var(--cb-f0fdf4)', borderRadius: 10, border: '1px solid var(--cb-bbf7d0)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <CreditCard size={15} color="var(--c-16a34a)" />
                  <span style={{ fontWeight: 600, fontSize: '.9rem' }}>Collect Payment</span>
                </div>
                <p style={{ fontSize: '.8rem', color: 'var(--c-166534)', margin: '0 0 10px' }}>
                  Plan rate: <strong>₹{customer.catv?.package?.price || customer.broadband?.plan?.price || '—'}/mo</strong> — pre-filled, edit if needed
                </p>
                <button className="btn btn-success" style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => setShowPayModal(true)}>
                  <Banknote size={15} /> Record Payment
                </button>
              </div>

              {/* Complaint */}
              <div style={{ padding: '14px', background: 'var(--cb-fff7ed)', borderRadius: 10, border: '1px solid var(--cb-fed7aa)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <MessageSquare size={15} color="var(--c-ea580c)" />
                  <span style={{ fontWeight: 600, fontSize: '.9rem' }}>Raise Complaint</span>
                </div>
                <p style={{ fontSize: '.8rem', color: 'var(--c-9a3412)', margin: '0 0 10px' }}>
                  Log a technical, billing, or service issue for this customer
                </p>
                <button className="btn btn-secondary" style={{ width: '100%', justifyContent: 'center', borderColor: 'var(--cb-fed7aa)' }}
                  onClick={() => setShowComplaintModal(true)}>
                  <MessageSquare size={15} /> New Complaint
                </button>
              </div>

              {/* Edit */}
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => navigate(`/customers/${customer._id}?edit=1`)}>
                Edit Customer Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showPayModal && customer && (
        <PaymentModal customer={customer} onClose={(success) => { setShowPayModal(false); if (success) setCustomer(null); }} />
      )}
      {showComplaintModal && customer && (
        <ComplaintModal customer={customer} onClose={(success) => { setShowComplaintModal(false); if (success) setCustomer(null); }} />
      )}
    </div>
  );
}

function InfoRow({ icon, label, value, mono, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid var(--border)', gap: 8 }}>
      <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        {icon}{label}
      </span>
      <span style={{ fontSize: '.82rem', fontWeight: bold ? 700 : 500, fontFamily: mono ? 'JetBrains Mono, monospace' : 'inherit', textAlign: 'right', wordBreak: 'break-all' }}>
        {value || '—'}
      </span>
    </div>
  );
}
